import clarity from '@microsoft/clarity';
import { Constants } from '../common/Constants';

export enum ClarityEvent {
  CONTAINER_VIEWED = 'container_viewed',
  AUTH_SUCCESS = 'auth_success',
  AUTH_ERROR = 'auth_error',
  NAVIGATION_ERROR = 'navigation_error'
}

export interface ClarityConfig {
  projectId: string;
  enabled: boolean;
  debug?: boolean;
}

export class ClarityService {
  private isInitialized = false;
  private config: ClarityConfig | null = null;
  private currentUserId: string | null = null;

  public init(): void {
    if (this.isInitialized) {
      console.warn('Clarity is already initialized');
      return;
    }

    this.config = {
      projectId: Constants.CLARITY_PROJECT_ID,
      enabled: Constants.isFeatureEnabled('CLARITY_ENABLED'),
      debug: Constants.isFeatureEnabled('CLARITY_DEBUG')
    };

    if (!this.config.enabled) {
      return;
    }

    if (!this.config.projectId) {
      console.error('Clarity project ID is required');
      return;
    }

    try {
      clarity.init(this.config.projectId);
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Clarity:', error);
    }
  }

  public identifyUser(userId: string): void {
    if (!this.isInitialized || !this.config?.enabled) {
      return;
    }

    try {
      // Use MS Clarity's identify method with MSAL user ID as consistent identifier
      clarity.identify(userId);
      this.currentUserId = userId;
    } catch (error) {
      console.error('Failed to identify user in Clarity:', error);
    }
  }

  public clearUser(): void {
    if (!this.isInitialized || !this.config?.enabled) {
      return;
    }

    try {
      this.currentUserId = null;
    } catch (error) {
      console.error('Failed to clear user from Clarity:', error);
    }
  }

  public trackEvent(eventName: ClarityEvent | string): void {
    if (!this.isInitialized || !this.config?.enabled) {
      return;
    }

    try {
      clarity.event(eventName);
    } catch (error) {
      console.error('Failed to track Clarity event:', error);
    }
  }

  public get isEnabled(): boolean {
    return this.isInitialized && this.config?.enabled === true;
  }

  public get configuration(): ClarityConfig | null {
    return this.config;
  }

  public get currentUser(): string | null {
    return this.currentUserId;
  }
}

export const clarityService = new ClarityService();

export const trackContainerView = () => {
  clarityService.trackEvent(ClarityEvent.CONTAINER_VIEWED);
};

export const trackAuthEvent = (success: boolean) => {
  const eventName = success ? ClarityEvent.AUTH_SUCCESS : ClarityEvent.AUTH_ERROR;
  clarityService.trackEvent(eventName);
};

export const trackNavigationError = () => {
  clarityService.trackEvent(ClarityEvent.NAVIGATION_ERROR);
};
